// apps/web/src/pages/Register.tsx

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { trpc } from "@/trpc/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { RegisterSchema, TRegisterSchema } from "@/lib/validators/auth";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Lock, Briefcase, Phone, Loader2, Eye, EyeOff } from "lucide-react";


const isFakeEmail = (email: string): boolean => {
  const fakeEmailPatterns = [
    /example\.(com|org|net)/i,
    /test@/i,
    /fake@/i,
    /dummy@/i,
    /sample@/i,
    /@example/i,
    /@test\./i,
    /@fake\./i,
    /tempmail/i,
    /throwaway/i,
    /mailinator/i,
    /guerrillamail/i,
  ];
  return fakeEmailPatterns.some(pattern => pattern.test(email));
};

const isFakePhone = (phone: string): boolean => {
  const cleanPhone = phone.replace(/[\s-]/g, '');

  const fakePhonePatterns = [
    /^0(88|99)123456$/,
    /^0(88|99)0{5,}$/,
    /^0(88|99)0{5,}\d$/,
    /^0(88|99)1{6,}$/,
    /^0(88|99)9{6,}$/,
    /^(1234567|7654321)/,
    /^0(88|99)(\d)\2{5,}$/,
  ];

  return fakePhonePatterns.some(pattern => pattern.test(cleanPhone));
};

const isTemplateName = (name: string): boolean => {
  const templateNames = [
    /john\s+doe/i,
    /jane\s+doe/i,
    /joe\s+bloggs/i,
    /test\s+user/i,
    /sample\s+name/i,
    /example\s+name/i,
    /your\s+name/i,
    /first\s+last/i,
    /fname\s+lname/i,
  ];
  return templateNames.some(pattern => pattern.test(name.trim()));
};

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const form = useForm<TRegisterSchema>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: { fullName: "", email: "", phoneOne: "", phoneTwo: "", password: "", confirmPassword: "" },
  });

  const { mutate, isLoading } = trpc.user.register.useMutation({
    onSuccess: (data) => {
      setIsSubmitting(false);
      login(data);
      toast.success("Account created successfully!");
      navigate("/onboarding/verify");
    },
    onError: (error) => {
      setIsSubmitting(false);
      toast.error(error.message);
      // Reset form if needed
      form.reset({
        ...form.getValues(),
        password: "",
        confirmPassword: ""
      });
    },
  });

  const onSubmit = (data: TRegisterSchema) => {
    if (!agreedToTerms) {
      setShowTermsError(true);
      toast.error("Please agree to the Terms and Conditions to continue");
      document.getElementById('terms-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (isTemplateName(data.fullName)) {
      toast.error("Please enter your real name, not a template name like 'John Doe'");
      form.setError("fullName", {
        type: "manual",
        message: "Please use your real name"
      });
      return;
    }

    if (isFakeEmail(data.email)) {
      toast.error("Please use a real email address, not a test or example email");
      form.setError("email", {
        type: "manual",
        message: "Please use a valid email address"
      });
      return;
    }

    if (isFakePhone(data.phoneOne)) {
      toast.error("Please enter a valid phone number");
      form.setError("phoneOne", {
        type: "manual",
        message: "This phone number appears to be invalid"
      });
      return;
    }

    if (data.phoneTwo && isFakePhone(data.phoneTwo)) {
      toast.error("Please enter a valid secondary phone number");
      form.setError("phoneTwo", {
        type: "manual",
        message: "This phone number appears to be invalid"
      });
      return;
    }

    console.log("Form submitted successfully with data:", data);

    setIsSubmitting(true);

    const sanitizedData = {
      ...data,
      email: data.email.trim().toLowerCase(),
    };

    mutate(sanitizedData);


    setTimeout(() => {
      if (isLoading) {
        toast.error("Registration is taking longer than expected. Please refresh and try logging in.");
      }
    }, 30000);
  };

  const onInvalid = (errors: any) => {
    console.error("Form validation failed:", errors);
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-80px)] py-6 px-4 sm:py-12 animate-fade-in">
      <Card className="w-full max-w-md glass">
        <CardHeader className="text-center space-y-2 p-4 sm:p-6">
          <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            Create Your Account
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">Join K & C Logistics to get started.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <Label>Full Name</Label>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Real full name" {...field} className="pl-10" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>Email</Label>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="email" placeholder="email@domain.com" {...field} className="pl-10" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phoneOne"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Primary Phone</Label>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input type="tel" placeholder="0888 123 456" {...field} className="pl-10" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneTwo"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Secondary Phone</Label>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input type="tel" placeholder="(Optional)" {...field} className="pl-10" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <Label>Password</Label>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          {...field}
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <Label>Confirm Password</Label>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          {...field}
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <Label>I am a...</Label>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                          <SelectTrigger className="pl-10">
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                        </div>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CLIENT">Client</SelectItem>
                        <SelectItem value="TRANSPORTER">Transporter</SelectItem>
                        <SelectItem value="FLEET_MANAGER">Fleet Manager (Company)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div
                id="terms-section"
                className={`flex items-start space-x-2 pt-2 p-3 rounded-md transition-all duration-300 ${showTermsError && !agreedToTerms
                  ? 'bg-destructive/10 border-2 border-destructive animate-pulse'
                  : 'bg-transparent border-2 border-transparent'
                  }`}
              >
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => {
                    setAgreedToTerms(checked as boolean);
                    if (checked) setShowTermsError(false);
                  }}
                  className="mt-1"
                />
                <label
                  htmlFor="terms"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowTermsModal(true);
                    }}
                    className="text-primary hover:underline font-semibold inline cursor-pointer bg-transparent border-none p-0"
                  >
                    Terms of Service
                  </button>
                  {" "}and{" "}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowPrivacyModal(true);
                    }}
                    className="text-primary hover:underline font-semibold inline cursor-pointer bg-transparent border-none p-0"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>
              {showTermsError && !agreedToTerms && (
                <p className="text-sm text-destructive font-medium -mt-2">
                  You must agree to the Terms and Conditions to create an account
                </p>
              )}

              <Button
                type="submit"
                variant="premium"
                className={`w-full !mt-6 transition-all duration-200 ${isLoading || isSubmitting ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                disabled={isLoading || isSubmitting}
              >
                {isLoading || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>

              <div className="mt-6 text-center text-sm">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold text-primary hover:underline">
                  Log in
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Terms of Service Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="text-xl font-bold">Terms of Service</DialogTitle>
            <DialogDescription>Last Updated: December 15, 2025</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[65vh] px-6 pb-6">
            <div className="space-y-6 text-sm leading-relaxed text-muted-foreground pr-4">

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">1. Introduction and Acceptance</h3>
                <p className="mb-2">
                  Welcome to <strong className="text-foreground">K&C Logistics</strong> ("Company", "we", "us", or "our"). These Terms and Conditions ("Terms") govern your access to and use of our website, mobile application, and logistics platform (collectively, the "Platform").
                </p>
                <p className="mb-2">
                  By registering for, accessing, or using our Platform, you ("User", "you") agree to be bound by these Terms, our Privacy Policy, and any other guidelines or policies we may publish. If you do not agree to these Terms, you must not access or use the Platform.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you and K&C Logistics, a company registered and operating under the laws of the <strong className="text-foreground">Republic of Malawi</strong>.
                </p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">2. Definitions</h3>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong className="text-foreground">"Platform"</strong>: The K&C Logistics digital marketplace connecting Clients with Transporters, Fleet Managers, and Fleet Drivers.</li>
                  <li><strong className="text-foreground">"Client"</strong>: A User who posts cargo delivery jobs ("Jobs") on the Platform.</li>
                  <li><strong className="text-foreground">"Transporter"</strong>: An independent User who accepts Jobs to transport cargo.</li>
                  <li><strong className="text-foreground">"Fleet Manager"</strong>: A User who manages multiple vehicles and drivers, secures jobs, and assigns them to Fleet Drivers.</li>
                  <li><strong className="text-foreground">"Fleet Driver"</strong>: A User who operates as part of a fleet managed by a Fleet Manager.</li>
                  <li><strong className="text-foreground">"Services"</strong>: The facilitation of logistics coordination, payment processing, and shipment tracking provided by the Platform.</li>
                  <li><strong className="text-foreground">"Cargo"</strong>: The goods, merchandise, or materials entrusted for shipment.</li>
                </ul>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">3. Eligibility & Registration</h3>
                <p className="mb-2">You must be at least 18 years of age and possess the legal capacity to enter into binding contracts under the laws of Malawi.</p>
                <p className="mb-2">You are solely responsible for maintaining the confidentiality of your account credentials.</p>
                <p>Transporters, Fleet Managers, and Fleet Drivers are subject to a mandatory verification process.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">4. User Obligations</h3>
                <h4 className="text-sm font-semibold text-foreground mb-1">4.1 Client Obligations</h4>
                <ul className="list-disc pl-5 space-y-1 mb-3">
                  <li>You warrant that you are the legal owner of the Cargo or have authority to ship it.</li>
                  <li>You must provide accurate details regarding the Cargo.</li>
                  <li>You agree not to ship prohibited, illegal, hazardous, or dangerous materials.</li>
                </ul>
                <h4 className="text-sm font-semibold text-foreground mb-1">4.2 Transporter Obligations</h4>
                <ul className="list-disc pl-5 space-y-1 mb-3">
                  <li>You must hold a valid driver's license and all necessary permits.</li>
                  <li>You must maintain your vehicle in a safe, roadworthy, and insured condition.</li>
                  <li>You consent to <strong className="text-foreground">real-time location tracking</strong> while a Job is "In Transit".</li>
                </ul>
                <h4 className="text-sm font-semibold text-foreground mb-1">4.3 Fleet Manager Obligations</h4>
                <ul className="list-disc pl-5 space-y-1 mb-3">
                  <li>You must register all vehicles under your fleet and ensure they meet safety and legal requirements.</li>
                  <li>You are responsible for verifying and managing your Fleet Drivers.</li>
                  <li>You consent to real-time tracking of all vehicles and drivers under your fleet.</li>
                </ul>
                <h4 className="text-sm font-semibold text-foreground mb-1">4.4 Fleet Driver Obligations</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>You must hold a valid driver's license and comply with your Fleet Manager's requirements.</li>
                  <li>You consent to <strong className="text-foreground">mandatory, automatic location tracking</strong> during trips.</li>
                </ul>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">5. Financial Terms</h3>
                <p className="mb-2">Transporters are required to pay a non-refundable <strong className="text-foreground">Booking Fee of MWK 50,000 per load</strong> to secure a Job.</p>
                <p className="mb-2"><strong className="text-foreground">Clients must pay Transporters directly</strong> upon successful delivery of the Cargo.</p>
                <p>K&C Logistics is not a party to the financial transaction between Client and Transporter regarding the shipping cost.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">6. Prohibited Conduct</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Use the Platform for any illegal or unauthorized purpose.</li>
                  <li>Transmit any viruses, malware, or other malicious code.</li>
                  <li>Attempt to gain unauthorized access to any portion of the Platform.</li>
                  <li>Impersonate any person or entity.</li>
                  <li>Use automated systems to extract data from the Platform.</li>
                </ul>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">7. Intellectual Property</h3>
                <p>The Platform and its entire contents are owned by K&C Logistics and protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to access and use the Platform.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">8. Limitation of Liability</h3>
                <p>K&C Logistics acts solely as an intermediary marketplace. We do not provide transportation services ourselves. Our total liability shall not exceed the amount you paid to us in the twelve (12) months preceding the claim, or MWK 100,000, whichever is less.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">9-14. Additional Terms</h3>
                <p className="mb-2">Sections covering Indemnification, Termination, Modifications, Governing Law, Severability, and Contact Information apply.</p>
                <p>These Terms are governed by the laws of the <strong className="text-foreground">Republic of Malawi</strong>.</p>
                <div className="mt-3 p-3 bg-muted/50 rounded-lg text-xs">
                  <p className="text-foreground font-semibold mb-1">K&C Logistics Legal Team</p>
                  <p>Email: legal@kclogistics.mw</p>
                  <p>Phone: +265 999 123 456</p>
                  <p>Address: City Centre, Lilongwe, Malawi</p>
                </div>
              </section>

            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="text-xl font-bold">Privacy Policy</DialogTitle>
            <DialogDescription>Last Updated: December 15, 2025</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[65vh] px-6 pb-6">
            <div className="space-y-6 text-sm leading-relaxed text-muted-foreground pr-4">

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">1. Introduction</h3>
                <p className="mb-2">
                  At <strong className="text-foreground">K&C Logistics</strong> ("we," "us," or "our"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile application (the "Platform").
                </p>
                <p>Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Platform.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">2. Information We Collect</h3>
                <h4 className="text-sm font-semibold text-foreground mb-1">2.1 Personal Data</h4>
                <p className="mb-2">We may collect personally identifiable information, such as your name, shipping address, email address, telephone number, and National ID number.</p>

                <h4 className="text-sm font-semibold text-foreground mb-1">2.2 Vehicle and Verification Data</h4>
                <p className="mb-2">For Transporters, Fleet Managers, and Fleet Drivers, we collect vehicle details, driver's license copies, vehicle registration documents, proof of ownership, and insurance certificates.</p>

                <h4 className="text-sm font-semibold text-foreground mb-1">2.3 Location Data (Geo-Location)</h4>
                <p className="mb-2">We collect and process real-time location data from Transporters and Fleet Drivers while they are "Online" and during active Jobs. For Fleet Drivers, location tracking is <strong className="text-foreground">mandatory and automatic</strong>.</p>

                <h4 className="text-sm font-semibold text-foreground mb-1">2.4 Fleet Management Data</h4>
                <p className="mb-2">For Fleet Managers, we collect additional information including fleet size, driver assignments, job dispatch records, and fleet performance metrics.</p>

                <h4 className="text-sm font-semibold text-foreground mb-1">2.5 Financial Data</h4>
                <p className="mb-2">We do not store your full credit card or bank account details. Financial transactions are processed by <strong className="text-foreground">PayChangu</strong>.</p>

                <h4 className="text-sm font-semibold text-foreground mb-1">2.6 Usage & Device Data</h4>
                <p>We automatically collect certain information including your IP address, browser type, operating system, access times, and device information.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">3. How We Use Your Information</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Create and manage your account and provide customer support.</li>
                  <li>Facilitate logistics and transportation services.</li>
                  <li>Enable Fleet Managers to manage fleet operations.</li>
                  <li>Process payments, refunds, and financial transactions securely.</li>
                  <li>Verify the identity of Transporters, Fleet Managers, and Fleet Drivers.</li>
                  <li>Send notifications regarding your account and Jobs.</li>
                  <li>Monitor and analyze usage patterns to improve your experience.</li>
                  <li>Prevent fraudulent transactions and protect against criminal activity.</li>
                </ul>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">4. Disclosure of Your Information</h3>
                <p className="mb-2">Your information may be disclosed to comply with legal requirements, shared with third-party service providers (e.g., PayChangu), visible to other Users you interact with on the Platform, or transferred during business transactions.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">5. Data Security</h3>
                <p>We use administrative, technical, and physical security measures to help protect your personal information, including encryption of data in transit and at rest, secure server infrastructure, and regular security audits.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">6. Data Retention</h3>
                <p>We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. When we no longer need your data, we will securely delete or anonymize it.</p>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">7. Your Data Protection Rights</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong className="text-foreground">Right to access</strong> - Request copies of your personal data.</li>
                  <li><strong className="text-foreground">Right to rectification</strong> - Request correction of inaccurate information.</li>
                  <li><strong className="text-foreground">Right to erasure</strong> - Request deletion of your personal data.</li>
                  <li><strong className="text-foreground">Right to restrict processing</strong> - Request restriction under certain conditions.</li>
                  <li><strong className="text-foreground">Right to object</strong> - Object to our processing of your data.</li>
                  <li><strong className="text-foreground">Right to data portability</strong> - Request data transfer.</li>
                </ul>
              </section>

              <Separator />

              <section>
                <h3 className="text-base font-semibold text-foreground mb-2">8-13. Additional Provisions</h3>
                <p className="mb-2">Sections covering Cookies, Third-Party Websites, Children's Privacy, International Data Transfers, and Changes to this Policy apply.</p>
                <div className="mt-3 p-3 bg-muted/50 rounded-lg text-xs">
                  <p className="text-foreground font-semibold mb-1">K&C Logistics Privacy Officer</p>
                  <p>Email: privacy@kclogistics.mw</p>
                  <p>Phone: +265 999 123 456</p>
                  <p>Address: City Centre, Lilongwe, Malawi</p>
                </div>
              </section>

            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Register;